/**
 * <AdvancedTable> — reusable table with saved views, search, sort,
 * column layout, group-by, grand-totals and CSV/XLSX/PDF exports.
 *
 * Column definition:
 *   {
 *     key: "doc_no",
 *     label: "Invoice #",
 *     width: 120,                    // default width (px)
 *     align: "left" | "center" | "right",
 *     sortable: true,                // default true
 *     groupable: true,               // enables in group-by picker
 *     sum: true,                     // include in grand-totals row (numeric)
 *     accessor: (row) => row.doc_no, // optional getter
 *     render: (row) => <span/>,      // JSX for cell rendering
 *     exportValue: (row) => 12.5,    // plain value for CSV/XLSX/PDF
 *     filterType: "text" | "select" | "number-range" | "date-range",
 *     filterOptions: [{ value, label }], // when filterType === "select"
 *   }
 */
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Input } from "@/components/ui/input";
import { Search as SearchIcon, ArrowUp, ArrowDown, ChevronDown, ChevronRight, Download, FileText, FileSpreadsheet, X, Filter as FilterIcon, Layers, GripHorizontal, Minimize2 as Move, Maximize2, EyeOff, Pin, PinOff, Copy as CopyIcon, SlidersHorizontal as SlidersIcon, Rows3 as Rows, Grid3x3 as GridIcon } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { useAuth } from "@/lib/auth";
import useTableState from "@/components/table/useTableState";
import SavedViewsBar from "@/components/table/SavedViewsBar";
import PresetsMenu from "@/components/table/PresetsMenu";
import ColumnsMenu from "@/components/table/ColumnsMenu";
import { exportCSV, exportXLSX, exportPDF } from "@/components/table/tableExport";
import { auditColumns } from "@/components/table/auditColumns";
import EmptyState from "@/components/EmptyState";

const DEFAULT_ALIGN_FOR_TYPE = { number: "right", currency: "right", date: "left", text: "left" };

// ═══════════════════════════ Helpers ═══════════════════════════

/**
 * Iter · Feb 2026 — Measure each column's actual rendered content
 * width across every visible cell, then push the max (plus a small
 * padding buffer) back into the layout via `setColumnWidth` so the
 * change persists.
 *
 * Uses a temporary style override that disables width constraints so
 * the browser reports the intrinsic width. Guaranteed to shrink even
 * columns whose stored width is currently >>> content.
 */
/**
 * Iter Feb2026 · Cycling "Fit" button.
 *
 * Modes cycled through per click:
 *   1. "content"  — shrink each column to the max content width across
 *                   its cells; ignores the header so titles can crop.
 *   2. "header"   — width = each column's title width (short columns
 *                   only; cells crop).
 *   3. "screen"   — equal distribution across the table's visible
 *                   width so no horizontal scroll.
 *   4. "reset"    — restore the last saved widths from user's layout
 *                   preferences.
 * setColumnWidth accepts an explicit width; passing `null` clears the
 * override and the useTableState hook falls back to the saved default.
 */
export function fitColumnsToMode({ mode, tableKey, columns, setColumnWidth }) {
  if (!setColumnWidth) return;
  const table = document.querySelector(`[data-testid="table-${tableKey}"]`);
  if (!table) return;
  const PAD = 24;
  const MIN = 40;
  const MAX = 600;
  const visible = (columns || []).filter((c) => !c.hidden);

  if (mode === "content") {
    for (const c of visible) {
      const cells = table.querySelectorAll(`[data-testid^="table-cell-${tableKey}-${c.key}-"]`);
      let maxW = 0;
      cells.forEach((el) => {
        const child = el.firstElementChild || el;
        const w = child.scrollWidth || 0;
        if (w > maxW) maxW = w;
      });
      if (maxW > 0) setColumnWidth(c.key, Math.min(MAX, Math.max(MIN, maxW + PAD)));
    }
    return;
  }

  if (mode === "header") {
    for (const c of visible) {
      const header = table.querySelector(`[data-testid="table-th-${tableKey}-${c.key}"]`);
      const child = header ? (header.firstElementChild || header) : null;
      const w = child ? (child.scrollWidth || 0) : 0;
      if (w > 0) setColumnWidth(c.key, Math.min(MAX, Math.max(MIN, w + PAD)));
    }
    return;
  }

  if (mode === "screen") {
    const container = table.closest("[data-scroll-x], .overflow-auto") || table.parentElement;
    const total = (container && container.clientWidth) || table.clientWidth || 1200;
    const each = Math.max(MIN, Math.floor(total / Math.max(1, visible.length)) - 4);
    for (const c of visible) setColumnWidth(c.key, each);
    return;
  }

  if (mode === "reset") {
    for (const c of visible) setColumnWidth(c.key, null);
    return;
  }
}

// Backwards-compat wrapper — some callers still import the old name.
export function fitColumnsToContent(args) {
  return fitColumnsToMode({ ...args, mode: "content" });
}

// Iter Feb 2026 · 4-mode cycling Fit button. Local state remembers
// where we are in the cycle so consecutive clicks step forward.
function FitCycleButton({ tableKey, columns, setColumnWidth, resetColumnWidths }) {
  const [modeIdx, setModeIdx] = useState(0);
  const MODES = [
    { key: "content", label: "Content", title: "Fit each column to its content · titles may crop" },
    { key: "header",  label: "Header",  title: "Fit each column to its title · cells may crop" },
    { key: "screen",  label: "Screen",  title: "Distribute columns evenly across the screen" },
    { key: "reset",   label: "Reset",   title: "Restore the saved column widths" },
  ];
  const cur = MODES[modeIdx % MODES.length];
  const next = MODES[(modeIdx + 1) % MODES.length];
  return (
    <button
      type="button"
      onClick={() => {
        if (cur.key === "reset") {
          resetColumnWidths?.();
        } else {
          fitColumnsToMode({ mode: cur.key, tableKey, columns, setColumnWidth });
        }
        setModeIdx((i) => (i + 1) % MODES.length);
      }}
      className="inline-flex items-center gap-1 h-8 px-2 border border-border text-xs hover:bg-muted rounded-none"
      data-testid={`table-fit-columns-${tableKey}`}
      title={`${cur.title} · next click: ${next.label}`}
    >
      <Move className="w-3.5 h-3.5" /> Fit · {cur.label}
    </button>
  );
}


// Oct 2026 round 8 · DensityButton. 3-way row-density switch in the
// AdvancedTable toolbar. Clicking cycles through comfortable →
// compact → dense → comfortable. Hover tooltip shows the next step so
// users don't have to guess what the click will do.
function DensityButton({ density, setDensity, tableKey }) {
  const MODES = [
    { key: "comfortable", label: "Comfortable", title: "Roomy rows · default" },
    { key: "compact",     label: "Compact",     title: "Tight rows · 50 % more visible" },
    { key: "dense",       label: "Dense",       title: "Tightest rows · maximum visible per screen" },
  ];
  const curIdx = Math.max(0, MODES.findIndex((m) => m.key === density));
  const cur = MODES[curIdx] || MODES[0];
  const next = MODES[(curIdx + 1) % MODES.length];
  return (
    <button
      type="button"
      onClick={() => setDensity(next.key)}
      className={`inline-flex items-center justify-center h-8 w-8 border text-xs rounded-none transition-colors ${density !== "comfortable" ? "border-primary text-primary bg-primary/10" : "border-border hover:bg-muted"}`}
      data-testid={`table-density-toggle-${tableKey}`}
      title={`Row density: ${cur.label}. ${cur.title}. Click to switch to ${next.label}.`}
      aria-label={`Row density: ${cur.label}. Click to switch to ${next.label}.`}
    >
      <Rows className="w-3.5 h-3.5" />
    </button>
  );
}

/**
 * Feb 2026 · GridlinesButton. One-tap "Excel-like" toggle next to the
 * density switch. Adds a persistent 1 px border on every cell for the
 * grid-y look spreadsheet users expect; default off so the minimal
 * app aesthetic is preserved for everyone else.
 */
function GridlinesButton({ gridlines, setGridlines, tableKey }) {
  return (
    <button
      type="button"
      onClick={() => setGridlines(!gridlines)}
      className={`inline-flex items-center justify-center h-8 w-8 border text-xs rounded-none transition-colors ${gridlines ? "border-primary text-primary bg-primary/10" : "border-border hover:bg-muted"}`}
      data-testid={`table-gridlines-toggle-${tableKey}`}
      aria-pressed={gridlines}
      aria-label={gridlines ? "Hide cell gridlines" : "Show cell gridlines"}
      title={gridlines ? "Hide cell gridlines (minimal look)" : "Show cell gridlines (Excel-like look)"}
    >
      <GridIcon className="w-3.5 h-3.5" />
    </button>
  );
}


// R268qqqq · Feb 2026 — Memoised row. React re-renders only the
// specific <tr> whose props changed (row, selected, columnsSig, …).
// When an inline edit optimistically splices a single row via
// `setRows(prev => prev.map(...))`, the 199 unchanged rows keep their
// previous React output. Measured savings: 30-50% off inline-edit
// paint on Aquarius Global's ~200-row viewport.
//
// Trade-off: `renderCell` and `columns` are *newly-created closures*
// on every parent render, but we intentionally exclude them from the
// equality check. Cell-level click handlers close over stable state
// (setRows from useState) so a "stale" closure still works correctly
// — the only exception would be if a closure captured a mutable ref
// from workspace-scoped state (e.g. wsMembers). Those change rarely
// and never mid-inline-edit.
const MemoRow = memo(function MemoRow({
  row, rowIndex, columns, columnsSig, selectable, selected,
  onRowClick, toggleOne, renderCell, renderRowActions,
  cellPadX, cellPadY, compact, wrapMode, freezeOffsets,
  virtualizeRows, tableKey,
  showRowNumbers,
  // R268tttt · Feb 2026 — Signals from parent that THIS specific row
  // is in an edit-in-progress state (e.g. Owner picker open). Only
  // the row whose id matches re-renders when the parent flips the
  // marker, leaving the other 199 rows untouched.
  activeRow,
  // Iter Feb2026 · Drag-to-reorder. When onRowDrop is set the row
  // becomes draggable and a drop target. dragDropGroupKey scopes drops
  // so a task can only be dropped within its own phase group.
  onRowDrop,
  dragDropGroupKey,
}) {
  const draggable = !!onRowDrop;
  const handleDragStart = draggable ? (e) => {
    e.dataTransfer.setData("text/x-row-id", row.id);
    e.dataTransfer.setData("text/x-row-group", dragDropGroupKey || "");
    e.dataTransfer.effectAllowed = "move";
  } : undefined;
  const handleDragOver = draggable ? (e) => {
    // Show a drop cursor + visual line hint.
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    e.currentTarget.classList.add("outline", "outline-1", "outline-primary", "outline-offset-[-1px]");
  } : undefined;
  const handleDragLeave = draggable ? (e) => {
    e.currentTarget.classList.remove("outline", "outline-1", "outline-primary", "outline-offset-[-1px]");
  } : undefined;
  const handleDrop = draggable ? (e) => {
    e.preventDefault();
    e.currentTarget.classList.remove("outline", "outline-1", "outline-primary", "outline-offset-[-1px]");
    const fromId = e.dataTransfer.getData("text/x-row-id");
    const fromGroup = e.dataTransfer.getData("text/x-row-group");
    if (!fromId || fromId === row.id) return;
    // Only allow drops within the same group (phase). Cross-phase moves
    // use the "Move to phase" menu on the task detail.
    if ((dragDropGroupKey || "") !== (fromGroup || "")) return;
    // Position: drop above or below based on the mouse Y relative to the row.
    const rect = e.currentTarget.getBoundingClientRect();
    const above = (e.clientY - rect.top) < rect.height / 2;
    onRowDrop(fromId, row.id, above ? "before" : "after");
  } : undefined;
  return (
    <tr
      className={`border-b border-border hover:bg-muted/30 transition-colors ${onRowClick ? "cursor-pointer" : ""} ${selected ? "bg-primary/5" : ""}`}
      onClick={onRowClick ? () => onRowClick(row) : undefined}
      data-testid={`table-row-${tableKey}-${row.id || rowIndex}`}
      draggable={draggable}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      style={virtualizeRows ? {
        // R267i · Feb 2026 — Native "virtualization" via CSS.
        contentVisibility: "auto",
        containIntrinsicSize: "0 36px",
        verticalAlign: "middle",
      } : { verticalAlign: "middle" }}
    >
      {/* Feb 2026 R291i · Row-number is the LEFTMOST pinned column.
          Opaque background (solid, no inherit) so content never
          bleeds through on horizontal scroll. Row-content cells use
          align-middle inside the renderer; this cell is centered too. */}
      {showRowNumbers && (
        <td
          className={`px-1 ${cellPadY} text-right text-[10px] text-muted-foreground/55 select-none tabular-nums align-middle`}
          style={{
            position: "sticky",
            left: 0,
            zIndex: 3,
            // Oct 2026 round 6 · Lock to exact 36 px so the checkbox cell
            // at `left: 36` butts up seamlessly — no sub-pixel slit for
            // the frozen Name column to peek through.
            width: 36,
            minWidth: 36,
            maxWidth: 36,
            // Oct 2026 round 5 · Portalwiz production still reporting
            // "content showing through behind the checkbox / cropped on
            // the left" after the backgroundColor + borderRight fixes.
            // Root cause: the <tr>'s `hover:bg-muted/30` and the
            // row-selection `bg-primary/5` win over the sticky cell's
            // inline backgroundColor on some browsers / themes because
            // Tailwind's bg classes carry higher specificity than the
            // inline shorthand once hover is engaged. Fix with an
            // `inset box-shadow` painted ON TOP of the backgroundColor
            // — insets can't be overridden by TR classes and guarantee
            // a fully-opaque fill no matter what the parent row does.
            backgroundColor: "hsl(var(--background))",
            boxShadow: "inset 0 0 0 2000px hsl(var(--background)), 1px 0 0 hsl(var(--border))",
            borderRight: "2px solid hsl(var(--border))",
          }}
          onClick={(e) => e.stopPropagation()}
          aria-label={`Row ${rowIndex + 1}`}
        >
          {rowIndex + 1}
        </td>
      )}
      {selectable && (
        <td
          className={`${cellPadX} ${cellPadY} align-middle`}
          style={{
            position: "sticky",
            left: showRowNumbers ? 36 : 0,
            zIndex: 3,
            width: 40,
            minWidth: 40,
            maxWidth: 40,
            backgroundColor: "hsl(var(--background))",
            boxShadow: "inset 0 0 0 2000px hsl(var(--background)), 1px 0 0 hsl(var(--border))",
            borderRight: "2px solid hsl(var(--border))",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <input
            type="checkbox"
            checked={selected || false}
            onChange={() => toggleOne(row.id)}
            data-testid={`table-select-row-${tableKey}-${row.id}`}
            aria-label="Select row"
          />
        </td>
      )}
      {columns.map((c, colIdx) => {
        const align = c.align || DEFAULT_ALIGN_FOR_TYPE[c.type] || "left";
        const wrapCls =
          wrapMode === "wrap"
            ? "whitespace-normal break-words [&_.truncate]:whitespace-normal [&_.truncate]:overflow-visible [&_.truncate]:text-clip [&_.line-clamp-1]:!line-clamp-none [&_.line-clamp-2]:!line-clamp-none"
            : wrapMode === "nowrap"
              ? "whitespace-nowrap"
              : "overflow-hidden";
        // Oct 2026 · The first non-sticky data column needs extra left
        // padding so when the horizontal scroll bar is engaged, its
        // content doesn't visually rub up against the sticky cells'
        // right border. 10 px keeps the title clear of the border and
        // still looks tight on wide screens. Scope to the first
        // non-frozen column; frozen columns handle their own offsets
        // via `freezeOffsets`.
        const isFirstScrollable = colIdx === 0 && !c.frozen;
        return (
          <td
            key={c.key}
            className={`${cellPadX} ${cellPadY} ${compact ? "text-[11px] leading-none" : "text-sm"}`}
            style={{
              // Feb 2026 · Per-column `wrap: true` overrides compact
              // nowrap and enforces a maxWidth so long content (e.g.
              // task titles) can flow across multiple lines instead of
              // stretching the column.
              // Oct 2026 · Even without `wrap: true`, compact-mode cells
              // need a `maxWidth` cap. Otherwise long single-line
              // content (`indrajeet.sawant@portalwiz.com`) auto-grows
              // the column well past its configured width, pushing
              // every sibling off-screen on mobile. Column can still
              // shrink below `c.width` on narrow viewports because we
              // don't pin `width` / `minWidth` in compact mode.
              ...(compact
                ? (c.wrap
                    ? { width: c.maxWidth || c.width || 240, maxWidth: c.maxWidth || c.width || 240 }
                    : (c.width ? { maxWidth: c.width } : {}))
                : { width: c.width, minWidth: c.width, maxWidth: wrapMode === "nowrap" ? undefined : c.width }),
              ...(c.frozen ? { position: "sticky", left: freezeOffsets[c.key], zIndex: 1, backgroundColor: "hsl(var(--background))" } : {}),
              ...(isFirstScrollable ? { paddingLeft: "16px" } : {}),
              textAlign: align,
              fontFamily: c.type === "number" || c.type === "currency" ? "var(--font-mono)" : undefined,
              // Feb 2026 R291i · Vertically center every cell by default
              // so inline pickers (status/priority dropdowns) and plain
              // text sit on the same baseline. Wrap-mode columns keep
              // top-align since multi-line text should hug the top.
              verticalAlign: (wrapMode === "wrap" || c.wrap) ? "top" : "middle",
              whiteSpace: c.wrap ? "normal" : (compact ? "nowrap" : undefined),
            }}
            data-col-key={c.key}
            data-testid={`table-cell-${tableKey}-${c.key}-${row.id || rowIndex}`}
          >
            <div className={wrapCls}>{renderCell(row, c)}</div>
          </td>
        );
      })}
      {renderRowActions && (
        <td className={`${cellPadX} ${cellPadY} text-right align-middle`} onClick={(e) => e.stopPropagation()}>
          {renderRowActions(row)}
        </td>
      )}
    </tr>
  );
}, (prev, next) => {
  // Custom equality: identity-compare row + primitive props.
  // Deliberately skip `columns`, `renderCell`, `renderRowActions`,
  // `onRowClick`, `toggleOne`, `freezeOffsets` — they're recreated
  // each parent render but their behaviour is stable per column set
  // (which `columnsSig` DOES compare). If real column layout changes
  // (show/hide, width, freeze), columnsSig changes → row re-renders.
  return (
    prev.row === next.row &&
    prev.columnsSig === next.columnsSig &&
    prev.selected === next.selected &&
    prev.selectable === next.selectable &&
    prev.compact === next.compact &&
    prev.cellPadY === next.cellPadY &&
    prev.cellPadX === next.cellPadX &&
    prev.wrapMode === next.wrapMode &&
    prev.virtualizeRows === next.virtualizeRows &&
    prev.rowIndex === next.rowIndex &&
    prev.tableKey === next.tableKey &&
    prev.activeRow === next.activeRow
  );
});




export default function AdvancedTable({
  tableKey,
  title,
  rows,
  columns: defaultColumns,
  defaultLayout,
  loading = false,
  emptyTitle = "No rows",
  emptyDescription = "Try clearing filters or adjusting your search.",
  emptyIcon,
  renderRowActions,
  onRowClick,
  pdfFooter,
  className = "",
  toolbarExtra,
  compact = false,
  includeAuditColumns = true,
  // Iter · Feb 2026 — Optional escape hatch that lets the parent page
  // include its own filter/pill state in the saved-view config. When
  // supplied, `extraLayoutState` is written into the view's `extra_state`
  // and `onApplyExtraLayoutState` is invoked when a view is applied.
  extraLayoutState = null,
  onApplyExtraLayoutState = null,
  // Iter 137 · optional multi-row selection.
  selectable = false,
  selectedIds,
  onSelectionChange,
  renderBulkBar,
  // R267i · Feb 2026 — Opt-in native browser virtualization via
  // `content-visibility: auto`. Modern browsers skip layout + paint
  // for off-screen rows so a table with 5000 rows stays 60 fps.
  // Preserves <tr>/<td> semantics so accessibility, keyboard nav,
  // and screen readers keep working. Pass `virtualizeRows` on grids
  // large enough to matter (Leads, Timesheet Advanced).
  virtualizeRows = false,
  // Feb 2026 R291f · Show a small faded "#" row-number gutter on the
  // left of each row. On by default so every AdvancedTable gets it;
  // callers that want to turn it off (e.g. embedded mini-tables) can
  // pass `showRowNumbers={false}`.
  showRowNumbers = true,
  // R268tttt · Feb 2026 — Per-row edit marker. Parent passes a
  // function `(row) => string | null`; the return value is included
  // in the row's memoisation prop bag. When it flips for a specific
  // row (e.g. click-to-mount Owner picker), only that row re-renders.
  activeRowFor = null,
  // Iter Feb2026 · Optional decoration next to a group header. Receives
  // (items, groupKey, groupByCol) and returns a ReactNode rendered
  // inline after the count.
  groupSummary = null,
  // Iter Feb2026 · Controlled group-by override — when set to a string,
  // AdvancedTable forces group-by to that column key regardless of the
  // internal state. Used by the Phases toggle in ProjectTasksSubTab.
  // Pass null / undefined for uncontrolled behaviour (default).
  groupByOverride = undefined,
  // Iter Feb2026 · Drag-to-reorder. Callback `(fromId, toId, position)`
  // where position is "before" or "after". When set, task rows become
  // HTML5 draggable and drop-target within the SAME group (identified
  // by `dragDropGroupKey`).
  onRowDrop = null,
  dragDropGroupKey = null,
}) {
  const { activeWorkspaceId } = useAuth();
  // Auto-append the four audit columns (hidden by default) unless the caller
  // has already provided any of them, or opts out via includeAuditColumns=false.
  const effectiveColumns = useMemo(() => {
    if (!includeAuditColumns) return defaultColumns;
    const existingKeys = new Set((defaultColumns || []).map((c) => c.key));
    const extras = auditColumns().filter((c) => !existingKeys.has(c.key));
    return [...(defaultColumns || []), ...extras];
  }, [defaultColumns, includeAuditColumns]);
  const state = useTableState({ tableKey, defaultColumns: effectiveColumns, defaultLayout, extraLayoutState, onApplyExtraLayoutState });
  const { columns, sort, filters, groupBy, search, wrapMode, setSearch, toggleSort, setFilterValue, clearFilters, setGroupBy, setWrapMode } = state;
  // Feb 2026 R291e · Options row collapse. Persisted per-table so a
  // user's preference sticks (power users keep it open; everyone else
  // sees a calm single-row toolbar).
  const [optionsOpen, _setOptionsOpen] = useState(() => {
    try { return localStorage.getItem(`advTable.optionsOpen.${tableKey}`) === "1"; }
    catch { return false; }
  });
  const setOptionsOpen = useCallback((v) => {
    _setOptionsOpen((prev) => {
      const next = typeof v === "function" ? v(prev) : v;
      try { localStorage.setItem(`advTable.optionsOpen.${tableKey}`, next ? "1" : "0"); } catch (e) { void e; }
      return next;
    });
  }, [tableKey]);
  // Oct 2026 round 6 · Per-user Bulk-mode toggle. The caller passes
  // `selectable=true` to opt the table in; the user then flips a switch
  // in the Options panel to show the checkbox gutter. Default off so
  // the normal reading view isn't cluttered with checkboxes. Persisted
  // per table × user. When the caller passes `selectable=false` we
  // short-circuit — no toggle, no checkboxes, no bulk bar.
  const [bulkMode, _setBulkMode] = useState(() => {
    try { return localStorage.getItem(`advTable.bulkMode.${tableKey}`) === "1"; }
    catch { return false; }
  });
  const setBulkMode = useCallback((v) => {
    _setBulkMode((prev) => {
      const next = typeof v === "function" ? v(prev) : v;
      try { localStorage.setItem(`advTable.bulkMode.${tableKey}`, next ? "1" : "0"); } catch (e) { void e; }
      return next;
    });
  }, [tableKey]);
  // Clear selections when bulk mode turns off so a hidden gutter can't
  // keep rows selected invisibly.
  useEffect(() => {
    if (!bulkMode && selectedIds && selectedIds.size > 0) {
      onSelectionChange?.(new Set());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulkMode]);
  const effectiveSelectable = selectable && bulkMode;
  // Oct 2026 round 8 · Row-density switch surfaced in the toolbar.
  // Three modes: `comfortable` (default), `compact` (py-[1px] + 11 px
  // text), `dense` (py-0 + 11 px text + even tighter horizontal
  // padding). Caller's `compact` prop seeds the initial value when no
  // user choice is stored. Persisted per table × user so a PM who
  // wants dense tasks and a comfortable timesheet gets both.
  const [density, _setDensity] = useState(() => {
    try {
      const v = localStorage.getItem(`advTable.density.${tableKey}`);
      if (v === "comfortable" || v === "compact" || v === "dense") return v;
    } catch (e) { void e; }
    return compact ? "compact" : "comfortable";
  });
  const setDensity = useCallback((v) => {
    _setDensity(() => {
      try { localStorage.setItem(`advTable.density.${tableKey}`, v); } catch (e) { void e; }
      return v;
    });
  }, [tableKey]);
  // `compact` chrome kicks in for both compact AND dense so the
  // toolbar/options padding, inner CSS overrides and column auto-fit
  // thresholds also tighten up.
  const effectiveCompact = density !== "comfortable";
  // Feb 2026 · Gridlines — opt-in "Excel-like" look where every cell
  // gets a 1px border on all sides. Default off (minimal) to match
  // the app's existing visual style; persisted per table × user so
  // a PM who wants grid-y tasks and clean projects gets both. Also
  // exposed as a data attribute on the table container so CSS can
  // handle sticky-first-column + grouped-row edge cases without
  // peppering every <td> with Tailwind classes.
  const [gridlines, _setGridlines] = useState(() => {
    try {
      const v = localStorage.getItem(`advTable.gridlines.${tableKey}`);
      if (v === "1") return true;
      if (v === "0") return false;
    } catch (e) { void e; }
    return false;
  });
  const setGridlines = useCallback((v) => {
    _setGridlines(() => {
      try { localStorage.setItem(`advTable.gridlines.${tableKey}`, v ? "1" : "0"); } catch (e) { void e; }
      return v;
    });
  }, [tableKey]);
  // Apply the controlled `groupByOverride` prop from the parent.
  useEffect(() => {
    if (groupByOverride === undefined) return;
    if ((groupByOverride || null) !== (groupBy || null)) {
      setGroupBy(groupByOverride || null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupByOverride]);

  // Search / filter / sort / group ────────────────────────────────────
  const filtered = useMemo(() => {
    let out = rows;
    // Search
    if (search) {
      const q = search.toLowerCase();
      out = out.filter((r) => columns.some((c) => {
        if (c.visible === false) return false;
        const v = c.accessor ? c.accessor(r) : r[c.key];
        return v != null && String(v).toLowerCase().includes(q);
      }));
    }
    // Filters
    for (const f of filters) {
      const col = columns.find((c) => c.key === f.key);
      if (!col) continue;
      out = out.filter((r) => {
        const v = col.accessor ? col.accessor(r) : r[f.key];
        return matchFilter(v, f.op, f.value);
      });
    }
    // Sort
    if (sort.length) {
      out = [...out].sort((a, b) => {
        for (const s of sort) {
          const col = columns.find((c) => c.key === s.key);
          const av = col?.accessor ? col.accessor(a) : a[s.key];
          const bv = col?.accessor ? col.accessor(b) : b[s.key];
          if (av == null && bv == null) continue;
          if (av == null) return s.dir === "asc" ? -1 : 1;
          if (bv == null) return s.dir === "asc" ? 1 : -1;
          if (av < bv) return s.dir === "asc" ? -1 : 1;
          if (av > bv) return s.dir === "asc" ? 1 : -1;
        }
        return 0;
      });
    }
    return out;
  }, [rows, columns, search, filters, sort]);

  // Group-by
  const groups = useMemo(() => {
    if (!groupBy) return null;
    const col = columns.find((c) => c.key === groupBy);
    if (!col) return null;
    const map = new Map();
    for (const r of filtered) {
      const key = col.accessor ? col.accessor(r) : r[groupBy];
      const k = key == null || key === "" ? "—" : String(key);
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(r);
    }
    return Array.from(map.entries()).map(([k, items]) => ({ key: k, items }));
  }, [filtered, groupBy, columns]);

  // Grand totals
  const grandTotals = useMemo(() => {
    const t = {};
    for (const col of columns) {
      if (col.visible === false || !col.sum) continue;
      let s = 0;
      for (const r of filtered) {
        const v = col.accessor ? col.accessor(r) : r[col.key];
        const n = Number(v);
        if (!Number.isNaN(n)) s += n;
      }
      t[col.key] = s;
    }
    return Object.keys(t).length ? t : null;
  }, [filtered, columns]);

  const visibleCols = columns.filter((c) => c.visible !== false);
  const groupableCols = columns.filter((c) => c.groupable);

  const doExport = (kind) => {
    const cols = visibleCols.map((c) => ({ ...c, label: c.label || c.key }));
    const filename = `${tableKey}-${new Date().toISOString().slice(0, 10)}`;
    if (kind === "csv") exportCSV({ columns: cols, rows: filtered, filename: filename + ".csv", grandTotals });
    else if (kind === "xlsx") exportXLSX({ columns: cols, rows: filtered, filename: filename + ".xlsx", sheetName: title || tableKey, grandTotals });
    else if (kind === "pdf") exportPDF({
      workspaceId: activeWorkspaceId, tableKey,
      title: title || tableKey,
      columns: cols, rows: filtered, footer: pdfFooter,
      grandTotals,
    });
  };

  return (
    <div className={`border border-border bg-background ${effectiveCompact ? "acq-tbl-compact" : ""} ${gridlines ? "adv-tbl-gridlines" : ""} ${className}`} data-testid={`advanced-table-${tableKey}`}>
      {/* Iter · Feb 2026 — Scoped CSS overrides for compact mode so
          any Select/Input/Button rendered inside a cell shrinks to
          match the tighter row height. */}
      {effectiveCompact && (
        <style>{`
          .acq-tbl-compact td button,
          .acq-tbl-compact td [role="combobox"],
          .acq-tbl-compact td [data-slot="select-trigger"] {
            height: 20px !important;
            min-height: 20px !important;
            padding-top: 0 !important;
            padding-bottom: 0 !important;
            font-size: 11px !important;
            line-height: 1 !important;
          }
          .acq-tbl-compact td input,
          .acq-tbl-compact td select,
          .acq-tbl-compact td textarea {
            height: 20px !important;
            min-height: 20px !important;
            padding-top: 0 !important;
            padding-bottom: 0 !important;
            font-size: 11px !important;
            line-height: 1 !important;
          }
          .acq-tbl-compact td .chip,
          .acq-tbl-compact td svg {
            transform: scale(0.9);
          }
        `}</style>
      )}
      {/* Feb 2026 · Gridlines mode — opt-in "spreadsheet" look where
          every cell gets a subtle horizontal + vertical border, giving
          the table an Excel-like appearance. Scoped to this table only
          via the `.adv-tbl-gridlines` wrapper. Uses `hsl(var(--border))`
          at reduced opacity so the lines read as a soft grid rather
          than heavy rules that distract from the data. */}
      {gridlines && (
        <style>{`
          .adv-tbl-gridlines thead > tr > th,
          .adv-tbl-gridlines tbody > tr > td {
            border-right: 1px solid hsl(var(--border)) !important;
            border-bottom: 1px solid hsl(var(--border)) !important;
          }
          /* Remove the right border on the last cell of each row so the
             table edge doesn't double-up with the wrapper border. */
          .adv-tbl-gridlines thead > tr > th:last-child,
          .adv-tbl-gridlines tbody > tr > td:last-child {
            border-right: none !important;
          }
          /* Group header rows look better without inner verticals. */
          .adv-tbl-gridlines tbody tr[data-testid*="table-group-"] > td {
            border-right: none !important;
          }
        `}</style>
      )}
      {/* Feb 2026 R293q · Reordered + icon-only toolbar. Search anchors
          the left, density group (density toggle + gridlines) sits next,
          then view group (SavedViewsBar inline — picker, save, overwrite,
          manage, reset), then Options icon, with the count floating
          right. Every control carries a tooltip; the row stays on a
          single line even on narrow screens. */}
      <div className={`flex items-center gap-1.5 flex-wrap ${effectiveCompact ? "px-2 py-1" : "px-3 py-2"} border-b border-border bg-muted/20`} data-testid={`table-toolbar-${tableKey}`}>
        <div className="relative flex-1 min-w-[180px] max-w-sm">
          <SearchIcon className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={`Search ${title || tableKey}…`}
            className="pl-8 h-8 rounded-none text-sm bg-background"
            data-testid={`table-search-${tableKey}`}
          />
        </div>
        <span className="mx-0.5 h-5 border-l border-border" aria-hidden="true" />
        {/* Density group — compact / grid-lines */}
        <DensityButton density={density} setDensity={setDensity} tableKey={tableKey} />
        <GridlinesButton gridlines={gridlines} setGridlines={setGridlines} tableKey={tableKey} />
        <span className="mx-0.5 h-5 border-l border-border" aria-hidden="true" />
        {/* View group — picker, save, overwrite, manage, reset */}
        <SavedViewsBar state={state} tableKey={tableKey} inline />
        <span className="mx-0.5 h-5 border-l border-border" aria-hidden="true" />
        <button
          type="button"
          onClick={() => setOptionsOpen((v) => !v)}
          className={`inline-flex items-center justify-center h-8 w-8 border text-xs ${optionsOpen ? "border-primary bg-primary text-primary-foreground" : "border-border bg-background hover:bg-muted"}`}
          data-testid={`table-options-toggle-${tableKey}`}
          aria-expanded={optionsOpen}
          aria-label="Toggle options panel"
          title="Show / hide filters, columns, group-by, export…"
        >
          <SlidersIcon className="w-3.5 h-3.5" />
        </button>
        {toolbarExtra}
        <span className="ml-auto text-[11px] text-muted-foreground whitespace-nowrap" data-testid={`table-count-${tableKey}`}>
          {filtered.length} of {rows.length} loaded
        </span>
      </div>
      {optionsOpen && (
        <div className={`flex items-center gap-2 flex-wrap ${effectiveCompact ? "px-2 py-1" : "px-3 py-2"} border-b border-border bg-muted/30`}
             data-testid={`table-options-panel-${tableKey}`}>
          <FiltersButton state={state} tableKey={tableKey} />
          <GroupByButton groupableCols={groupableCols} groupBy={groupBy} setGroupBy={setGroupBy} tableKey={tableKey} />
          <ColumnsMenu state={state} tableKey={tableKey} />
          <PresetsMenu state={state} tableKey={tableKey} />
          <WrapModeButton wrapMode={wrapMode} setWrapMode={setWrapMode} tableKey={tableKey} />
          <FitCycleButton
            tableKey={tableKey}
            columns={columns}
            setColumnWidth={state.setColumnWidth}
            resetColumnWidths={state.resetColumnWidths}
          />
          <ExportButton onExport={doExport} tableKey={tableKey} />
          {/* Oct 2026 round 6 · Bulk-mode toggle. Only rendered when the
              caller opts into selection via `selectable`. Off by default
              — keeps the row-number gutter clean until a user needs to
              act on many rows at once. */}
          {selectable && (
            <label
              className={`inline-flex items-center gap-1.5 border px-2 py-1 h-7 text-[11px] cursor-pointer select-none ${bulkMode ? "border-primary bg-primary/10 text-primary" : "border-border bg-background hover:bg-muted"}`}
              title="Toggle multi-row selection checkboxes"
              data-testid={`table-bulk-mode-toggle-${tableKey}`}
            >
              <input
                type="checkbox"
                className="h-3 w-3 accent-primary"
                checked={bulkMode}
                onChange={(e) => setBulkMode(e.target.checked)}
                aria-label="Toggle bulk mode"
              />
              Bulk mode
            </label>
          )}
        </div>
      )}

      {/* Iter 137 · Bulk-action bar (shown when rows are selected) */}
      {effectiveSelectable && renderBulkBar && selectedIds && selectedIds.size > 0 && (
        <div className="border border-primary/40 bg-primary/5 px-3 py-2 flex items-center gap-2 flex-wrap"
             data-testid={`table-bulk-bar-${tableKey}`}>
          <span className="text-xs font-semibold" data-testid={`table-bulk-count-${tableKey}`}>
            {selectedIds.size} selected
          </span>
          <span className="text-[10px] text-muted-foreground">·</span>
          {renderBulkBar(rows.filter((r) => selectedIds.has(r.id)))}
          <button
            type="button"
            className="ml-auto text-[11px] text-muted-foreground hover:text-foreground underline"
            onClick={() => onSelectionChange?.(new Set())}
            data-testid={`table-bulk-clear-${tableKey}`}
          >Clear</button>
        </div>
      )}

      {/* Body */}
      {/* R64 · Fix sticky headers (recurring bug).
           Root cause: `overflow-x-auto` alone makes this div a scroll
           container in BOTH axes (browsers coerce y=visible→auto when
           x=auto), so `position: sticky; top: 0` on <th> sticks to the
           top of THIS div — which itself scrolls with the page —
           making the header invisibly scroll away.
           Fix: give this div its own `max-height` so it becomes a
           self-contained vertical scroll container. Sticky <thead> now
           stays pinned to the top of the visible panel while the
           tbody scrolls internally. */}
      <div className="overflow-auto max-h-[calc(100vh-220px)]">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3" role="status" aria-live="polite" data-testid={`table-loading-${tableKey}`}>
            <div className="relative w-12 h-12">
              <div className="absolute inset-0 rounded-full border-4 border-primary/20" />
              <div className="absolute inset-0 rounded-full border-4 border-primary border-t-transparent animate-spin" />
            </div>
            <div className="text-sm font-semibold text-foreground">Loading {title || "rows"}…</div>
            <div className="text-[11px] text-muted-foreground">Fetching the freshest data — one moment.</div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-6">
            <EmptyState title={emptyTitle} description={emptyDescription} icon={emptyIcon} testid={`table-empty-${tableKey}`} />
          </div>
        ) : (
          <TableBody
            tableKey={tableKey}
            columns={visibleCols}
            groups={groups}
            groupBy={groupBy}
            groupSummary={groupSummary}
            rows={filtered}
            sort={sort}
            onSort={toggleSort}
            grandTotals={grandTotals}
            renderRowActions={renderRowActions}
            onRowClick={onRowClick}
            compact={effectiveCompact}
            density={density}
            wrapMode={wrapMode}
            onResizeColumn={state.setColumnWidth}
            onReorderColumns={state.reorderColumns}
            onColumnVisibility={state.setColumnVisibility}
            onToggleFrozen={state.toggleFrozen}
            selectable={effectiveSelectable}
            selectedIds={selectedIds}
            onSelectionChange={onSelectionChange}
            virtualizeRows={virtualizeRows}
            showRowNumbers={showRowNumbers}
            activeRowFor={activeRowFor}
            onRowDrop={onRowDrop}
            dragDropGroupKey={dragDropGroupKey}
          />
        )}
      </div>
    </div>
  );
}

// ═══════════════════ TABLE BODY ═══════════════════

function TableBody({ tableKey, columns, rows, groups, groupBy, groupSummary, sort, onSort, grandTotals, renderRowActions, onRowClick, compact, density = "comfortable", wrapMode = "crop", onResizeColumn, onReorderColumns, onColumnVisibility, onToggleFrozen, selectable, selectedIds, onSelectionChange, virtualizeRows = false, showRowNumbers = false, activeRowFor = null, onRowDrop = null, dragDropGroupKey = null }) {
  // Oct 2026 round 6 · Pre-column gutter widths. Row-number is a fixed
  // 36 px column, the bulk-select checkbox is 40 px. We now bake these
  // widths into the frozen-column `freezeOffsets` so any `frozen: true`
  // data column sits AFTER the gutters instead of overlapping them at
  // left: 0. Previously Leads' frozen "Name" column shared left:0 with
  // the row-number cell — its content peeked through any sub-pixel gap
  // on horizontal scroll (reported as "content showing behind the
  // checkbox"). Also freezes gutter widths with explicit inline styles
  // so Tailwind `w-9`/`w-10` can't be stretched by content/padding.
  const ROW_NUM_W = 36;
  const CHECKBOX_W = 40;
  const gutterOffset = (showRowNumbers ? ROW_NUM_W : 0) + (selectable ? CHECKBOX_W : 0);
  // Freeze offsets
  const frozenCols = columns.filter((c) => c.frozen);
  const freezeOffsets = {};
  let acc = gutterOffset;
  for (const c of frozenCols) { freezeOffsets[c.key] = acc; acc += (c.width || 120); }
  // Oct 2026 round 8 · 3-way row density. `compact` is the legacy
  // boolean; `density` is the explicit mode. Keep both so pages that
  // haven't switched over yet still render correctly.
  const _density = density || (compact ? "compact" : "comfortable");
  const cellPadY =
    _density === "dense" ? "py-0" :
    _density === "compact" ? "py-[1px]" :
    "py-2";
  // Oct 2026 · Give compact-mode cells 6 px horizontal padding instead
  // of 4 px. The 4-px gap sat flush against the sticky checkbox's
  // 1-px right-shadow and the user reported the first task column
  // "cropped on the left / visible behind the checkbox" on Portalwiz
  // production. Two extra pixels of breathing room on each side
  // removes the overlap without materially widening the row.
  const cellPadX =
    _density === "dense" ? "px-1" :
    _density === "compact" ? "px-1.5" :
    "px-2";

  // Selection helpers.
  const allVisibleIds = useMemo(() =>
    rows.map((r) => r.id).filter(Boolean), [rows]);
  const selectedCount = allVisibleIds.filter((id) => selectedIds?.has(id)).length;
  const allSelected = selectable && allVisibleIds.length > 0 && selectedCount === allVisibleIds.length;
  const someSelected = selectable && selectedCount > 0 && !allSelected;
  // R268vvvv · Feb 2026 — Fix regression where MemoRow skipped the
  // `toggleOne` prop from its equality check, so unchanged rows kept
  // their FIRST-render `toggleOne` closure — which captured an empty
  // `selectedIds`. Clicking a second checkbox wiped the first. The
  // fix: park `selectedIds` in a ref that's rewritten on every render.
  // `toggleOne` reads the ref, so the closure is stable AND always
  // sees the latest selection.
  const selectedRef = useRef(selectedIds);
  selectedRef.current = selectedIds;
  const toggleAll = useCallback(() => {
    const cur = selectedRef.current;
    const next = new Set(cur || []);
    const visIds = allVisibleIds;
    const isAll = visIds.length > 0 && visIds.every((id) => cur?.has(id));
    if (isAll) {
      visIds.forEach((id) => next.delete(id));
    } else {
      visIds.forEach((id) => next.add(id));
    }
    onSelectionChange?.(next);
  }, [allVisibleIds, onSelectionChange]);
  const toggleOne = useCallback((id) => {
    const next = new Set(selectedRef.current || []);
    if (next.has(id)) next.delete(id); else next.add(id);
    onSelectionChange?.(next);
  }, [onSelectionChange]);

  // Local resize state — during a drag we manipulate the <th> width via
  // direct DOM writes (bypassing React) so the drag feels snappy. Only
  // the final width is committed to state on mouseup. Prevents the
  // 60fps state churn that made the resize handle feel sluggish.
  const resizingRef = useRef(null); // { key, startX, startWidth, thEl, didDrag }
  const [_resizingKey, setResizingKey] = useState(null); // visual cue only
  // Guard: when the user just dragged the resize handle, browsers still
  // fire a synthetic `click` on the <th> once mouseup lands. Without
  // this flag that click flips the sort. Set on mouseup; cleared on the
  // very next tick.
  const didResizeRef = useRef(false);
  // Feb 2026 · Track which resize handle the cursor is currently hovering
  // so keyboard shortcuts ([ and ]) can nudge that column ±20px without
  // needing a click first.
  const hoveredColRef = useRef(null);
  // Ref to the table root so we can measure real cell widths for
  // auto-fit + scope keyboard listeners.
  const rootRef = useRef(null);

  // Auto-fit: sniff the widest rendered cell in the given column and set
  // the column width to that (plus padding). Uses `scrollWidth` which
  // reflects the ideal content width even when the cell is truncated by
  // the current column width.
  const autoFitColumn = useCallback((key) => {
    const root = rootRef.current;
    if (!root) return;
    const cells = root.querySelectorAll(`[data-col-key="${CSS.escape(key)}"]`);
    if (!cells.length) return;
    let max = 60;
    cells.forEach((el) => {
      // Use scrollWidth of the innermost content so truncated text still
      // reports its natural width.
      const inner = el.firstElementChild || el;
      const w = Math.max(el.scrollWidth, inner.scrollWidth || 0);
      if (w > max) max = w;
    });
    // Add a small buffer for cell padding + the resize handle strip.
    const target = Math.min(1000, max + 24);
    onResizeColumn?.(key, target);
  }, [onResizeColumn]);

  // Keyboard nudge: [ shrinks the hovered column by 20px, ] widens it.
  // Only active when the user isn't typing in an input / textarea and
  // when the resize handle for that column is under the cursor.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "[" && e.key !== "]") return;
      const key = hoveredColRef.current;
      if (!key) return;
      // Skip when focus is inside a form control so users can still
      // type square brackets in filter inputs / rename editors.
      const active = document.activeElement;
      const tag = active?.tagName?.toLowerCase();
      if (tag === "input" || tag === "textarea" || active?.isContentEditable) return;
      e.preventDefault();
      const th = rootRef.current?.querySelector(`th[data-testid$="-${key}"]`);
      const cur = th ? th.getBoundingClientRect().width : 120;
      const delta = e.key === "]" ? 20 : -20;
      const next = Math.max(60, Math.min(1000, cur + delta));
      onResizeColumn?.(key, next);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onResizeColumn]);

  const beginResize = (e, col) => {
    e.stopPropagation();
    e.preventDefault();
    // Locate the enclosing <th> element for direct DOM manipulation.
    const thEl = e.currentTarget.closest("th");
    const startWidth = thEl ? thEl.getBoundingClientRect().width : (col.width || 120);
    resizingRef.current = {
      key: col.key,
      startX: e.clientX,
      startWidth,
      thEl,
      didDrag: false,
      rafId: 0,
      pendingWidth: startWidth,
    };
    setResizingKey(col.key);
    const applyRaf = () => {
      const st = resizingRef.current;
      if (!st) return;
      st.rafId = 0;
      if (st.thEl) {
        st.thEl.style.width = st.pendingWidth + "px";
        st.thEl.style.minWidth = st.pendingWidth + "px";
      }
    };
    const onMove = (ev) => {
      const st = resizingRef.current;
      if (!st) return;
      const dx = ev.clientX - st.startX;
      if (Math.abs(dx) > 2) st.didDrag = true;
      const next = Math.max(60, Math.min(1000, st.startWidth + dx));
      st.pendingWidth = next;
      // rAF-throttle DOM writes to at most one per frame.
      if (!st.rafId) st.rafId = window.requestAnimationFrame(applyRaf);
    };
    const onUp = () => {
      const st = resizingRef.current;
      if (!st) return;
      if (st.rafId) window.cancelAnimationFrame(st.rafId);
      if (st.didDrag) {
        // Commit the final width to state ONCE (persists via useTableState).
        onResizeColumn?.(st.key, st.pendingWidth);
        didResizeRef.current = true;
        // Clear the guard after the synthetic click has fired.
        window.setTimeout(() => { didResizeRef.current = false; }, 0);
      }
      resizingRef.current = null;
      setResizingKey(null);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  // Native drag-and-drop for inline header reorder (draggable columns).
  const [dragKey, setDragKey] = useState(null);
  const [overKey, setOverKey] = useState(null);
  const onDragStart = (e, key) => {
    setDragKey(key);
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", key); } catch { /* fx */ }
  };
  const onDragOver = (e, key) => {
    if (!dragKey || key === dragKey) return;
    e.preventDefault();
    setOverKey(key);
  };
  const onDrop = (e, targetKey) => {
    e.preventDefault();
    if (dragKey && targetKey && dragKey !== targetKey) {
      onReorderColumns?.(dragKey, targetKey);
    }
    setDragKey(null);
    setOverKey(null);
  };
  const onDragEnd = () => { setDragKey(null); setOverKey(null); };

  const headerRow = (
    <tr style={{ backgroundColor: "var(--table-header-bg)" }} data-testid={`table-header-${tableKey}`}>
      {showRowNumbers && (
        // Oct 2026 round 5 · Inset box-shadow guarantees an opaque
        // fill that no TR hover / selected-row class can override.
        <th className={`border-b border-border px-1 ${cellPadY} text-[9px] uppercase tracking-widest text-muted-foreground/70 text-right`}
            style={{
              position: "sticky",
              left: 0,
              top: 0,
              zIndex: 6,
              // Oct 2026 round 6 · Lock gutter cell to 36 px so the
              // checkbox column at `left: 36` can't open a sub-pixel
              // slit through which a frozen column's content peeks.
              width: 36,
              minWidth: 36,
              maxWidth: 36,
              // Feb 2026 · `--table-header-bg` resolves to a subtle
              // workspace-brand tint set in index.css; keeps the
              // header row visually tied to the workspace identity.
              backgroundColor: "var(--table-header-bg)",
              boxShadow: "inset 0 0 0 2000px var(--table-header-bg)",
              borderRight: "2px solid hsl(var(--border))",
            }}
            aria-label="Row number">
          #
        </th>
      )}
      {selectable && (
        <th className={`border-b border-border ${cellPadX} ${cellPadY}`}
            style={{
              position: "sticky",
              left: showRowNumbers ? 36 : 0,
              top: 0,
              zIndex: 5,
              width: 40,
              minWidth: 40,
              maxWidth: 40,
              backgroundColor: "var(--table-header-bg)",
              boxShadow: "inset 0 0 0 2000px var(--table-header-bg)",
              borderRight: "2px solid hsl(var(--border))",
            }}>
          <input
            type="checkbox"
            checked={allSelected}
            ref={(el) => { if (el) el.indeterminate = someSelected; }}
            onChange={toggleAll}
            data-testid={`table-select-all-${tableKey}`}
            aria-label="Select all"
          />
        </th>
      )}
      {columns.map((c, colIdx) => {
        const s = sort.find((x) => x.key === c.key);
        // R267i · Feb 2026 — Multi-sort: show a small priority index
        // when the sort stack has > 1 entry so users see the order.
        const sortIdx = s ? sort.findIndex((x) => x.key === c.key) : -1;
        const showPriority = sort.length > 1 && sortIdx >= 0;
        const align = c.align || DEFAULT_ALIGN_FOR_TYPE[c.type] || "left";
        const isDropTarget = overKey === c.key && dragKey && dragKey !== c.key;
        // Oct 2026 · Extra left padding on the first non-frozen header
        // so the label stays clear of the sticky-cell border, mirroring
        // the body-cell fix.
        const isFirstScrollable = colIdx === 0 && !c.frozen;
        return (
          <th
            key={c.key}
            style={{
              // Iter · Feb 2026 — Compact mode drops width constraints
              // so browser auto-shrinks each column to its text width.
              // Comfortable mode keeps the user-configured widths.
              // `wrap: true` cols still cap width in compact so text
              // wraps within a sensible bound.
              // Oct 2026 · Also cap non-wrap compact columns at their
              // configured width so a long single-line value doesn't
              // stretch the column past its sibling layout — matches
              // the body-cell fix for the Portalwiz mobile bug.
              ...(compact
                ? (c.wrap
                    ? { width: c.maxWidth || c.width || 240, maxWidth: c.maxWidth || c.width || 240 }
                    : (c.width ? { maxWidth: c.width } : {}))
                : { width: c.width, minWidth: c.width }),
              // R60 · Every header cell is sticky-top so it survives page
              // scroll (relying on <thead sticky> alone is inconsistent
              // across browsers, especially when the wrapping div has
              // `overflow-x-auto` which becomes a scroll container).
              position: "sticky",
              top: 0,
              zIndex: c.frozen ? 4 : 3,
              backgroundColor: "var(--table-header-bg)",
              ...(c.frozen ? { left: freezeOffsets[c.key] } : {}),
              ...(isFirstScrollable ? { paddingLeft: "16px" } : {}),
              textAlign: align,
              boxShadow: isDropTarget ? "inset 3px 0 0 0 hsl(var(--primary))" : undefined,
              opacity: dragKey === c.key ? 0.4 : 1,
              whiteSpace: compact ? "nowrap" : undefined,
            }}
            className={`relative border-b border-border ${cellPadX} ${cellPadY} ${compact ? "text-[9px]" : "text-[10px]"} uppercase tracking-widest font-semibold group ${s ? "text-primary" : "text-muted-foreground"} ${c.sortable !== false ? "cursor-pointer select-none hover:text-foreground" : ""}`}
            onClick={(e) => {
              // Ignore clicks that started on the resize handle
              if (e.target?.closest?.('[data-resize="1"]')) return;
              // Feb 2026 · Guard against sort-vs-resize misfire. When a
              // real drag just happened on the handle, the browser
              // still fires a synthetic click on the <th>. Skip sort
              // in that case.
              if (didResizeRef.current) return;
              // R267i · Feb 2026 — Shift-click appends to the sort
              // stack instead of replacing it, so power users can
              // build multi-column orders like "replied desc, then
              // temperature desc" in-place.
              if (c.sortable !== false) onSort(c.key, { additive: e.shiftKey });
            }}
            onContextMenu={(e) => {
              e.preventDefault();
              setCtxMenu({ x: e.clientX, y: e.clientY, col: c });
            }}
            title={c.sortable !== false ? "Click to sort · Shift+click to add secondary sort" : undefined}
            draggable={!c.frozen}
            onDragStart={(e) => {
              // Iter 247 · If the drag started on the resize handle, cancel it
              // — the whole <th> is draggable for column reorder but the resize
              // strip needs the browser NOT to hijack the mouse for HTML5 drag.
              // Use .closest() so the inner visible-line child of the handle
              // still counts as "resize origin".
              if (e.target?.closest?.('[data-resize="1"]')) { e.preventDefault(); return; }
              onDragStart(e, c.key);
            }}
            onDragOver={(e) => onDragOver(e, c.key)}
            onDrop={(e) => onDrop(e, c.key)}
            onDragEnd={onDragEnd}
            data-testid={`table-th-${tableKey}-${c.key}`}
          >
            <span className="inline-flex items-center gap-1">
              {!c.frozen && (
                <GripHorizontal className="w-3 h-3 opacity-0 group-hover:opacity-40 cursor-move" aria-hidden />
              )}
              {c.label || c.key}
              {s?.dir === "asc" && <ArrowUp className="w-3.5 h-3.5 text-primary" data-testid={`table-th-sort-arrow-${tableKey}-${c.key}`} />}
              {s?.dir === "desc" && <ArrowDown className="w-3.5 h-3.5 text-primary" data-testid={`table-th-sort-arrow-${tableKey}-${c.key}`} />}
              {/* Feb 2026 · Always show the priority badge when sorted
                  (even for single-key sort) so users see the sort order
                  clearly. Multi-sort just adds the numeric index. */}
              {s && (
                <span className="ml-0.5 px-1 text-[9px] bg-primary/15 text-primary rounded-none font-mono border border-primary/30"
                       data-testid={`table-th-sort-priority-${tableKey}-${c.key}`}>
                  {sort.length > 1 ? sortIdx + 1 : (s.dir === "asc" ? "A→Z" : "Z→A")}
                </span>
              )}
            </span>
            {/* Resize handle — wider 12px hitbox with a 2px always-visible
                line that thickens to 3px + primary color on hover / during
                drag. Whole handle zone also picks up a subtle primary tint
                on hover so users see they're grabbing something. Double-
                click auto-fits the column. Hidden in compact mode
                (widths are ignored there). */}
            {/* Feb 2026 · Resize handle rendered in every density — the
                handle is a 3px absolutely-positioned overlay, so hiding
                it in compact mode (as we used to) just removed the
                feature without saving any space. */}
            <span
              data-resize="1"
              onMouseDown={(e) => beginResize(e, c)}
              onDoubleClick={(e) => { e.stopPropagation(); autoFitColumn(c.key); }}
              onClick={(e) => { e.stopPropagation(); }}
              onDragStart={(e) => { e.preventDefault(); e.stopPropagation(); }}
              onMouseEnter={() => { hoveredColRef.current = c.key; }}
              onMouseLeave={() => { if (hoveredColRef.current === c.key) hoveredColRef.current = null; }}
              draggable={false}
              className={`absolute top-0 right-0 h-full w-3 cursor-col-resize z-20 flex items-center justify-end pr-0.5 group/rh transition-colors ${_resizingKey === c.key ? "bg-primary/10" : "hover:bg-primary/10"}`}
              data-testid={`table-th-resize-${tableKey}-${c.key}`}
              title="Drag to resize · double-click to auto-fit · [ / ] to nudge ±20px">
              <span className={`h-3/5 transition-all ${_resizingKey === c.key ? "w-[3px] bg-primary" : "w-0.5 bg-border/70 group-hover/rh:w-[3px] group-hover/rh:bg-primary"}`} />
            </span>
          </th>
        );
      })}
      {renderRowActions && <th className={`border-b border-border ${cellPadX} ${cellPadY} w-16`} />}
    </tr>
  );

  const renderCell = (r, c) => {
    if (typeof c.render === "function") return c.render(r);
    const v = c.accessor ? c.accessor(r) : r[c.key];
    return v == null ? "" : v;
  };

  // R268qqqq · Feb 2026 — Signature of the current column list so
  // <MemoRow> can quickly detect real column changes (show/hide, add,
  // rename) without doing a deep compare on the whole array. When
  // this string is unchanged, every unchanged row keeps its previous
  // React output — mutating one row via `setRows(prev.map(...))` now
  // re-renders exactly one <tr> instead of the whole tbody.
  const columnsSig = useMemo(
    () => columns.map((c) => c.key + (c.width || "") + (c.align || "") + (c.frozen ? "F" : "")).join("|"),
    [columns],
  );

  const rowMarkup = (r, i) => (
    <MemoRow
      key={r.id || i}
      row={r}
      rowIndex={i}
      columns={columns}
      columnsSig={columnsSig}
      selectable={selectable}
      selected={selectable && selectedIds?.has(r.id)}
      onRowClick={onRowClick}
      toggleOne={toggleOne}
      renderCell={renderCell}
      renderRowActions={renderRowActions}
      cellPadX={cellPadX}
      cellPadY={cellPadY}
      compact={compact}
      wrapMode={wrapMode}
      freezeOffsets={freezeOffsets}
      virtualizeRows={virtualizeRows}
      showRowNumbers={showRowNumbers}
      tableKey={tableKey}
      activeRow={activeRowFor ? activeRowFor(r) : null}
      onRowDrop={onRowDrop}
      dragDropGroupKey={
        typeof dragDropGroupKey === "function" ? dragDropGroupKey(r) : dragDropGroupKey
      }
    />
  );

  // Kept for parity with existing code paths (grouped view etc.).
  // Removed: `_legacyRowMarkup` inline body — MemoRow now handles all
  // row rendering, including inside <GroupSection>.

  // Feb 2026 · Right-click context menu on column headers. Consolidates
  // Sort ↑/↓, Auto-fit, Hide, Freeze, Copy-name into a single place so
  // users don't hunt for these actions across separate menus.
  const [ctxMenu, setCtxMenu] = useState(null); // { x, y, col }
  useEffect(() => {
    if (!ctxMenu) return;
    const close = () => setCtxMenu(null);
    window.addEventListener("click", close);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    window.addEventListener("keydown", (e) => e.key === "Escape" && close());
    return () => {
      window.removeEventListener("click", close);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [ctxMenu]);

  return (
    <>
    <table ref={rootRef} className={`${compact ? "w-auto min-w-full" : "w-full"} border-collapse text-sm`} data-testid={`table-${tableKey}`}>
      <thead className="sticky top-0 z-10">{headerRow}</thead>
      <tbody>
        {groups ? groups.map((g) => (
          <GroupSection key={g.key} label={g.key} items={g.items} colCount={columns.length + (renderRowActions ? 1 : 0) + (selectable ? 1 : 0) + (showRowNumbers ? 1 : 0)} rowMarkup={rowMarkup} tableKey={tableKey} groupSummary={groupSummary} groupBy={groupBy} />
        )) : rows.map(rowMarkup)}
      </tbody>
      {grandTotals && (
        <tfoot>
          <tr className="bg-primary text-primary-foreground font-bold" data-testid={`table-grand-total-${tableKey}`}>
            {showRowNumbers && <td className="w-9" />}
            {selectable && <td className="w-10" />}
            {columns.map((c, i) => {
              const align = c.align || DEFAULT_ALIGN_FOR_TYPE[c.type] || "left";
              return (
                <td
                  key={c.key}
                  className="px-2 py-2 text-sm"
                  style={{
                    width: c.width, minWidth: c.width,
                    ...(c.frozen ? { position: "sticky", left: freezeOffsets[c.key], background: "hsl(var(--primary))" } : {}),
                    textAlign: align,
                    fontFamily: "var(--font-mono)",
                  }}
                >
                  {i === 0 ? "Σ TOTAL" : (grandTotals[c.key] != null ? (typeof grandTotals[c.key] === "number" ? grandTotals[c.key].toLocaleString(undefined, { maximumFractionDigits: 2 }) : grandTotals[c.key]) : "")}
                </td>
              );
            })}
            {renderRowActions && <td />}
          </tr>
        </tfoot>
      )}
    </table>
    {ctxMenu && (
      <ColumnHeaderContextMenu
        x={ctxMenu.x}
        y={ctxMenu.y}
        col={ctxMenu.col}
        sortDir={(sort || []).find((s) => s.key === ctxMenu.col.key)?.dir}
        tableKey={tableKey}
        onSortAsc={() => { onSort(ctxMenu.col.key); if ((sort || []).find((s) => s.key === ctxMenu.col.key)?.dir !== "asc") onSort(ctxMenu.col.key); }}
        onSortDesc={() => {
          // Ensure descending regardless of current state — toggle up to twice.
          const cur = (sort || []).find((s) => s.key === ctxMenu.col.key);
          if (!cur) { onSort(ctxMenu.col.key); onSort(ctxMenu.col.key); }
          else if (cur.dir === "asc") onSort(ctxMenu.col.key);
          // if already desc, no-op
        }}
        onAutoFit={() => autoFitColumn(ctxMenu.col.key)}
        onHide={() => onColumnVisibility?.(ctxMenu.col.key, false)}
        onToggleFreeze={() => onToggleFrozen?.(ctxMenu.col.key)}
        onClose={() => setCtxMenu(null)}
      />
    )}
  </>
  );
}

function GroupSection({ label, items, colCount, rowMarkup, tableKey, groupSummary, groupBy }) {
  const [open, setOpen] = useState(true);
  const summary = groupSummary ? groupSummary(items, label, groupBy) : null;
  return (
    <>
      <tr className="bg-muted/60" data-testid={`table-group-${tableKey}-${label}`}>
        <td colSpan={colCount} className="px-3 py-1.5 font-semibold text-sm cursor-pointer" onClick={() => setOpen((v) => !v)}>
          <span className="inline-flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1">
              {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
              {label} <span className="text-muted-foreground font-normal">· {items.length}</span>
            </span>
            {summary && <span className="ml-1" onClick={(e) => e.stopPropagation()}>{summary}</span>}
          </span>
        </td>
      </tr>
      {open && items.map(rowMarkup)}
    </>
  );
}

// ═══════════════════ TOOLBAR BITS ═══════════════════

function FiltersButton({ state, tableKey }) {
  const { columns, filters, setFilterValue, clearFilters } = state;
  const filterable = columns.filter((c) => c.filterType);
  const activeCount = filters.length;
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-1 border px-2 py-1 text-xs rounded-none ${activeCount ? "border-primary text-primary bg-primary/10" : "border-border hover:bg-muted"} ${open ? "ring-1 ring-primary/40" : ""}`}
        data-testid={`table-filters-btn-${tableKey}`}
      >
        <FilterIcon className="w-3.5 h-3.5" /> Filters{activeCount ? ` (${activeCount})` : ""}
      </button>
      {open && (
        <FiltersFloatingPanel
          tableKey={tableKey}
          filterable={filterable}
          filters={filters}
          setFilterValue={setFilterValue}
          clearFilters={clearFilters}
          activeCount={activeCount}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

/**
 * Feb 2026 · Draggable + resizable floating filter panel.
 *
 * Why not a Popover any more?  The Popover anchored to the Filters
 * button and would clip badly on tall tables with many filterable
 * columns (Projects page screenshot showed the panel escaping its
 * container, overlapping the sidebar, and losing its "Clear" footer
 * off-screen).  A floating panel rendered via portal:
 *
 *   • escapes any `overflow: hidden` parent so it is never clipped
 *   • is draggable from its header so a user can pull it over
 *     empty table space while they tweak filters
 *   • is resizable from the bottom-right so dense pages (22+
 *     filterable columns) can be given room, and narrow pages
 *     shrunk out of the way
 *   • is NON-modal (no backdrop) so the table underneath stays
 *     interactive and the user can see rows refresh live as they
 *     type — this was the whole point of the user's request
 *   • persists its position + size per `tableKey` in localStorage
 *     so power users don't re-arrange it every time
 *
 * No new deps: pointer events + rAF for smooth 60fps drag/resize.
 */
function FiltersFloatingPanel({ tableKey, filterable, filters, setFilterValue, clearFilters, activeCount, onClose }) {
  const panelRef = useRef(null);
  const dragStateRef = useRef(null);
  const rafRef = useRef(0);
  const lsKey = `advTable.filtersPanel.${tableKey}`;

  // Starting geometry: centred-ish, sized to fit most pages, clamped
  // to the viewport.  Hydrated once from localStorage.
  const [geom, setGeom] = useState(() => {
    const defaultW = Math.min(880, Math.max(320, (typeof window !== "undefined" ? window.innerWidth : 1024) - 80));
    const defaultH = Math.min(560, Math.max(260, (typeof window !== "undefined" ? window.innerHeight : 720) - 160));
    const vw = typeof window !== "undefined" ? window.innerWidth : 1024;
    const vh = typeof window !== "undefined" ? window.innerHeight : 720;
    const defaults = {
      x: Math.max(16, Math.round((vw - defaultW) / 2)),
      y: Math.max(72, Math.round((vh - defaultH) / 2)),
      w: defaultW,
      h: defaultH,
    };
    try {
      const raw = localStorage.getItem(lsKey);
      if (!raw) return defaults;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== "object") return defaults;
      // Re-clamp into viewport so a saved layout from a wider monitor
      // doesn't push the panel off-screen on a laptop.
      const w = Math.max(300, Math.min(parsed.w || defaults.w, vw - 32));
      const h = Math.max(220, Math.min(parsed.h || defaults.h, vh - 80));
      const x = Math.max(8, Math.min(parsed.x ?? defaults.x, vw - w - 8));
      const y = Math.max(56, Math.min(parsed.y ?? defaults.y, vh - h - 8));
      return { x, y, w, h };
    } catch { return defaults; }
  });

  // Persist position / size (debounced via rAF so dragging doesn't
  // thrash localStorage every pointer tick).
  useEffect(() => {
    const id = window.setTimeout(() => {
      try { localStorage.setItem(lsKey, JSON.stringify(geom)); } catch { /* storage quota */ }
    }, 200);
    return () => window.clearTimeout(id);
  }, [geom, lsKey]);

  // Close on Escape.
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Re-clamp when the window resizes (user rotates tablet, snaps
  // window to half-screen, etc.).
  useEffect(() => {
    const onResize = () => {
      setGeom((g) => {
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const w = Math.min(g.w, vw - 32);
        const h = Math.min(g.h, vh - 80);
        const x = Math.max(8, Math.min(g.x, vw - w - 8));
        const y = Math.max(56, Math.min(g.y, vh - h - 8));
        return { x, y, w, h };
      });
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const beginDrag = useCallback((e, mode) => {
    // Ignore right-clicks + touches on form inputs so filter typing
    // never accidentally initiates a drag.
    if (e.button != null && e.button !== 0) return;
    e.preventDefault();
    dragStateRef.current = {
      mode,                                // "move" | "resize"
      startX: e.clientX,
      startY: e.clientY,
      origX: geom.x,
      origY: geom.y,
      origW: geom.w,
      origH: geom.h,
    };
    const onMove = (ev) => {
      if (!dragStateRef.current) return;
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        const d = dragStateRef.current;
        if (!d) return;
        const dx = ev.clientX - d.startX;
        const dy = ev.clientY - d.startY;
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        if (d.mode === "move") {
          const x = Math.max(8, Math.min(d.origX + dx, vw - d.origW - 8));
          const y = Math.max(56, Math.min(d.origY + dy, vh - d.origH - 8));
          setGeom((g) => ({ ...g, x, y }));
        } else {
          const w = Math.max(320, Math.min(d.origW + dx, vw - d.origX - 8));
          const h = Math.max(220, Math.min(d.origH + dy, vh - d.origY - 8));
          setGeom((g) => ({ ...g, w, h }));
        }
      });
    };
    const onUp = () => {
      dragStateRef.current = null;
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    };
    document.body.style.userSelect = "none";
    document.body.style.cursor = mode === "move" ? "grabbing" : "nwse-resize";
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  }, [geom.x, geom.y, geom.w, geom.h]);

  const resetGeom = () => {
    try { localStorage.removeItem(lsKey); } catch { /* ignore */ }
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const w = Math.min(880, vw - 80);
    const h = Math.min(560, vh - 160);
    setGeom({
      x: Math.max(16, Math.round((vw - w) / 2)),
      y: Math.max(72, Math.round((vh - h) / 2)),
      w, h,
    });
  };

  return createPortal(
    <div
      ref={panelRef}
      role="dialog"
      aria-label="Table filters"
      className="fixed z-[80] bg-background border border-border shadow-2xl rounded-none flex flex-col"
      style={{ left: geom.x, top: geom.y, width: geom.w, height: geom.h }}
      data-testid={`table-filters-panel-${tableKey}`}
    >
      {/* Header — drag handle. Grabby cursor so the affordance is clear. */}
      <div
        onPointerDown={(e) => beginDrag(e, "move")}
        className="flex items-center gap-2 px-3 py-2 border-b border-border bg-muted/60 cursor-grab active:cursor-grabbing select-none"
        data-testid={`table-filters-panel-header-${tableKey}`}
      >
        <GripHorizontal className="w-4 h-4 text-muted-foreground" />
        <FilterIcon className="w-3.5 h-3.5 text-primary" />
        <span className="text-xs font-semibold tracking-wide uppercase">Filters</span>
        {activeCount > 0 && (
          <span className="text-[10px] font-mono px-1.5 py-0.5 bg-primary text-primary-foreground"
                data-testid={`table-filters-panel-count-${tableKey}`}>
            {activeCount} active
          </span>
        )}
        <div className="ml-auto flex items-center gap-1">
          <button
            type="button"
            onClick={resetGeom}
            title="Reset panel size & position"
            className="text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground px-1.5 py-0.5 border border-transparent hover:border-border"
            data-testid={`table-filters-panel-reset-${tableKey}`}
          >
            Reset
          </button>
          <button
            type="button"
            onClick={onClose}
            title="Close filters (Esc)"
            className="text-muted-foreground hover:text-foreground p-1"
            data-testid={`table-filters-panel-close-${tableKey}`}
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Body — scrollable grid of filter inputs. */}
      <div className="flex-1 min-h-0 overflow-y-auto p-3"
           data-testid={`table-filters-panel-body-${tableKey}`}>
        {filterable.length === 0 ? (
          <div className="text-xs text-muted-foreground italic p-2">No filterable columns.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-2.5">
            {filterable.map((c) => {
              const current = filters.find((f) => f.key === c.key);
              return (
                <div key={c.key} className="min-w-0">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-0.5 truncate">{c.label || c.key}</div>
                  <FilterInput col={c} value={current?.value} onChange={(op, v) => setFilterValue(c.key, op, v)} testid={`table-filter-${tableKey}-${c.key}`} />
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer — active count + Clear */}
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-t border-border bg-muted/30">
        <span className="text-[11px] text-muted-foreground">
          {activeCount > 0
            ? `${activeCount} filter${activeCount === 1 ? "" : "s"} active`
            : "Tip: drag the header to move • drag the corner to resize"}
        </span>
        {activeCount > 0 && (
          <button
            type="button"
            onClick={clearFilters}
            className="text-[11px] underline text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
            data-testid={`table-clear-filters-${tableKey}`}
          >
            <X className="w-3 h-3" /> Clear all filters
          </button>
        )}
      </div>

      {/* Resize handle (bottom-right). Pure CSS triangle so no extra icon. */}
      <div
        onPointerDown={(e) => beginDrag(e, "resize")}
        className="absolute right-0 bottom-0 w-4 h-4 cursor-nwse-resize group"
        style={{ touchAction: "none" }}
        data-testid={`table-filters-panel-resize-${tableKey}`}
        aria-label="Resize filters panel"
      >
        <span className="absolute right-0.5 bottom-0.5 block w-3 h-3 border-r-2 border-b-2 border-muted-foreground/50 group-hover:border-primary transition-colors" />
      </div>
    </div>,
    document.body
  );
}

function FilterInput({ col, value, onChange, testid }) {
  if (col.filterType === "select" && Array.isArray(col.filterOptions)) {
    const selected = value || [];
    return (
      <div className="flex flex-wrap gap-1" data-testid={testid}>
        {col.filterOptions.map((o) => {
          const on = selected.includes(o.value);
          return (
            <button
              key={o.value}
              type="button"
              onClick={() => {
                const next = on ? selected.filter((x) => x !== o.value) : [...selected, o.value];
                onChange("in", next);
              }}
              className={`text-[10px] px-1.5 py-0.5 border ${on ? "border-primary bg-primary/10 text-primary" : "border-border"}`}
              data-testid={`${testid}-opt-${o.value}`}
            >
              {on ? "✓ " : ""}{o.label}
            </button>
          );
        })}
      </div>
    );
  }
  if (col.filterType === "number-range") {
    const [min, max] = value || [null, null];
    return (
      <div className="flex items-center gap-1" data-testid={testid}>
        <Input type="number" placeholder="min" value={min ?? ""} onChange={(e) => onChange("between", [e.target.value === "" ? null : Number(e.target.value), max])} className="h-8 rounded-none text-xs" />
        <span className="text-muted-foreground">–</span>
        <Input type="number" placeholder="max" value={max ?? ""} onChange={(e) => onChange("between", [min, e.target.value === "" ? null : Number(e.target.value)])} className="h-8 rounded-none text-xs" />
      </div>
    );
  }
  if (col.filterType === "date-range") {
    const [from, to] = value || [null, null];
    return (
      <div className="flex items-center gap-1" data-testid={testid}>
        <Input type="date" value={from || ""} onChange={(e) => onChange("between", [e.target.value || null, to])} className="h-8 rounded-none text-xs" />
        <span className="text-muted-foreground">–</span>
        <Input type="date" value={to || ""} onChange={(e) => onChange("between", [from, e.target.value || null])} className="h-8 rounded-none text-xs" />
      </div>
    );
  }
  // text
  return (
    <Input
      value={value || ""}
      onChange={(e) => onChange("contains", e.target.value)}
      placeholder="contains…"
      className="h-8 rounded-none text-xs"
      data-testid={testid}
    />
  );
}

function GroupByButton({ groupableCols, groupBy, setGroupBy, tableKey }) {
  if (groupableCols.length === 0) return null;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`inline-flex items-center gap-1 border px-2 py-1 text-xs rounded-none ${groupBy ? "border-primary text-primary bg-primary/10" : "border-border hover:bg-muted"}`}
          data-testid={`table-groupby-btn-${tableKey}`}
        >
          <Layers className="w-3.5 h-3.5" /> {groupBy ? `Group: ${groupableCols.find((c) => c.key === groupBy)?.label || groupBy}` : "Group by"}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-56 p-1 rounded-none" data-testid={`table-groupby-menu-${tableKey}`}>
        <button
          type="button"
          onClick={() => setGroupBy(null)}
          className={`w-full text-left text-sm px-2.5 py-1.5 hover:bg-muted ${!groupBy ? "font-semibold" : ""}`}
          data-testid={`table-groupby-${tableKey}-none`}
        >
          No grouping
        </button>
        {groupableCols.map((c) => (
          <button
            key={c.key}
            type="button"
            onClick={() => setGroupBy(c.key)}
            className={`w-full text-left text-sm px-2.5 py-1.5 hover:bg-muted ${groupBy === c.key ? "font-semibold" : ""}`}
            data-testid={`table-groupby-${tableKey}-${c.key}`}
          >
            {c.label || c.key}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}

// Iter 247 · Row-wrap mode toggle (Wrap · Crop · Don't wrap). Persisted in
// per-user table layout so users don't lose their preference.
function WrapModeButton({ wrapMode, setWrapMode, tableKey }) {
  const modes = [
    { key: "wrap",   label: "Wrap",       hint: "Multi-line cells" },
    { key: "crop",   label: "Crop",       hint: "Ellipsis (default)" },
    { key: "nowrap", label: "Don't wrap", hint: "Single line, no ellipsis" },
  ];
  const active = modes.find((m) => m.key === wrapMode) || modes[1];
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1 border border-border px-2 py-1 text-xs hover:bg-muted rounded-none"
          title={`Row wrap: ${active.label}`}
          data-testid={`table-wrap-btn-${tableKey}`}
        >
          <Layers className="w-3.5 h-3.5" /> {active.label}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-56 p-1 rounded-none" data-testid={`table-wrap-menu-${tableKey}`}>
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground px-2.5 py-1.5">Row wrap</div>
        {modes.map((m) => (
          <button
            key={m.key}
            type="button"
            onClick={() => setWrapMode(m.key)}
            className={`w-full text-left text-sm px-2.5 py-1.5 hover:bg-muted flex items-start gap-2 ${wrapMode === m.key ? "bg-primary/10" : ""}`}
            data-testid={`table-wrap-${m.key}-${tableKey}`}
          >
            <span className={`w-4 mt-0.5 text-primary ${wrapMode === m.key ? "opacity-100" : "opacity-0"}`}>✓</span>
            <span className="flex-1">
              <div className="font-medium">{m.label}</div>
              <div className="text-[10px] text-muted-foreground">{m.hint}</div>
            </span>
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}

function ExportButton({ onExport, tableKey }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1 border border-border px-2 py-1 text-xs hover:bg-muted rounded-none"
          data-testid={`table-export-btn-${tableKey}`}
        >
          <Download className="w-3.5 h-3.5" /> Export
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-40 p-1 rounded-none" data-testid={`table-export-menu-${tableKey}`}>
        <button type="button" onClick={() => onExport("csv")} className="w-full text-left text-sm px-2.5 py-1.5 hover:bg-muted inline-flex items-center gap-2" data-testid={`table-export-csv-${tableKey}`}>
          <FileText className="w-3.5 h-3.5" /> CSV
        </button>
        <button type="button" onClick={() => onExport("xlsx")} className="w-full text-left text-sm px-2.5 py-1.5 hover:bg-muted inline-flex items-center gap-2" data-testid={`table-export-xlsx-${tableKey}`}>
          <FileSpreadsheet className="w-3.5 h-3.5" /> Excel (XLSX)
        </button>
        <button type="button" onClick={() => onExport("pdf")} className="w-full text-left text-sm px-2.5 py-1.5 hover:bg-muted inline-flex items-center gap-2" data-testid={`table-export-pdf-${tableKey}`}>
          <FileText className="w-3.5 h-3.5" /> PDF
        </button>
      </PopoverContent>
    </Popover>
  );
}

// ═══════════════════ MATCHER ═══════════════════

function matchFilter(v, op, target) {
  if (target == null || target === "" || (Array.isArray(target) && target.length === 0)) return true;
  switch (op) {
    case "eq": return v === target;
    case "ne": return v !== target;
    case "contains": return v != null && String(v).toLowerCase().includes(String(target).toLowerCase());
    case "in": return Array.isArray(target) && target.includes(v);
    case "gt": return Number(v) > Number(target);
    case "lt": return Number(v) < Number(target);
    case "gte": return Number(v) >= Number(target);
    case "lte": return Number(v) <= Number(target);
    case "between": {
      const [a, b] = target || [];
      if (a != null && v < a) return false;
      if (b != null && v > b) return false;
      return true;
    }
    default: return true;
  }
}


// ═══════════════════ COLUMN HEADER CONTEXT MENU ═══════════════════
// Feb 2026 · Right-click on any column header opens this. Consolidates
// Sort A→Z / Z→A · Auto-fit · Hide · Freeze / Unfreeze · Copy name so
// users don't have to hunt across separate menus. Positioned at the
// mouse coords, closes on outside click / Escape / scroll / resize.
function ColumnHeaderContextMenu({ x, y, col, sortDir, tableKey, onSortAsc, onSortDesc, onAutoFit, onHide, onToggleFreeze, onClose }) {
  const run = (fn) => (e) => {
    e.stopPropagation();
    try { fn?.(); } catch { /* noop */ }
    onClose?.();
  };
  const copyName = (e) => {
    e.stopPropagation();
    try { navigator.clipboard?.writeText(col.label || col.key); } catch { /* noop */ }
    onClose?.();
  };
  // Clamp position so the menu doesn't overflow the viewport.
  const vw = typeof window !== "undefined" ? window.innerWidth : 1200;
  const vh = typeof window !== "undefined" ? window.innerHeight : 800;
  const style = {
    position: "fixed",
    left: Math.min(x, vw - 220),
    top: Math.min(y, vh - 260),
    zIndex: 60,
  };
  const isSortable = col.sortable !== false;
  return (
    <div
      style={style}
      className="w-52 border border-border bg-background shadow-lg"
      onClick={(e) => e.stopPropagation()}
      onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); }}
      data-testid={`table-th-ctx-${tableKey}-${col.key}`}
    >
      <div className="px-3 py-1.5 text-[9px] uppercase tracking-widest text-muted-foreground font-semibold border-b border-border truncate">
        {col.label || col.key}
      </div>
      {isSortable && (
        <>
          <CtxItem icon={ArrowUp} label="Sort A → Z" testid={`ctx-sort-asc-${col.key}`} onClick={run(onSortAsc)} active={sortDir === "asc"} />
          <CtxItem icon={ArrowDown} label="Sort Z → A" testid={`ctx-sort-desc-${col.key}`} onClick={run(onSortDesc)} active={sortDir === "desc"} />
          <div className="h-px bg-border" />
        </>
      )}
      <CtxItem icon={Maximize2} label="Auto-fit width" testid={`ctx-autofit-${col.key}`} onClick={run(onAutoFit)} />
      <CtxItem icon={col.frozen ? PinOff : Pin} label={col.frozen ? "Unfreeze column" : "Freeze column"} testid={`ctx-freeze-${col.key}`} onClick={run(onToggleFreeze)} />
      <div className="h-px bg-border" />
      <CtxItem icon={EyeOff} label="Hide column" testid={`ctx-hide-${col.key}`} onClick={run(onHide)} tone="danger" />
      <CtxItem icon={CopyIcon} label="Copy column name" testid={`ctx-copy-${col.key}`} onClick={copyName} />
    </div>
  );
}

function CtxItem({ icon: Icon, label, onClick, active = false, tone = null, testid }) {
  const cls = tone === "danger" ? "hover:bg-rose-500/10 hover:text-rose-700" : "hover:bg-primary/10 hover:text-primary";
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left ${cls} ${active ? "bg-primary/5 text-primary font-semibold" : ""}`}
      data-testid={testid}
    >
      {Icon && <Icon className="w-3.5 h-3.5 shrink-0" />}
      <span className="truncate">{label}</span>
      {active && <span className="ml-auto text-[9px]">●</span>}
    </button>
  );
}
